import { useState } from 'react';

import { X } from 'lucide-react';

export interface WidgetPreviewBranding {
  title?: string;
  subtitle?: string;
  logo_url?: string;
  logo_style?: string;
  primary_color?: string;
  welcome_message?: string;
  tool_display_mode?: 'inline' | 'overlay';
}

interface Props {
  branding: WidgetPreviewBranding;
  configName?: string;
}

function parseStyleString(style?: string): React.CSSProperties {
  if (!style) return {};
  const result: Record<string, string> = {};
  style.split(';').forEach((decl) => {
    const idx = decl.indexOf(':');
    if (idx === -1) return;
    const rawKey = decl.slice(0, idx).trim();
    const value = decl.slice(idx + 1).trim();
    if (!rawKey || !value) return;
    const key = rawKey.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
    result[key] = value;
  });
  return result as React.CSSProperties;
}

export function WidgetPreview({ branding, configName }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [bubbleDismissed, setBubbleDismissed] = useState(false);

  const pc = branding.primary_color || '#2563eb';
  const hasLogo = !!branding.logo_url;
  const logoExtra = parseStyleString(branding.logo_style);
  const title = branding.title || configName || 'Talk to us';
  const welcome = branding.welcome_message;

  return (
    <div className="relative w-full overflow-hidden rounded-xl border border-slate-800 bg-[#0b1220]">
      <div className="flex items-center justify-between border-b border-slate-800 px-4 py-2">
        <span className="text-xs font-semibold tracking-wide text-slate-400 uppercase">
          Live Preview
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setIsOpen((v) => !v);
              if (!isOpen) setBubbleDismissed(true);
            }}
            className="rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-800"
          >
            {isOpen ? 'Close panel' : 'Open panel'}
          </button>
          {bubbleDismissed && !isOpen && (
            <button
              type="button"
              onClick={() => setBubbleDismissed(false)}
              className="rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-800"
            >
              Replay bubble
            </button>
          )}
        </div>
      </div>

      <div
        className="relative h-[520px] w-full"
        style={{
          backgroundImage:
            'radial-gradient(circle at 25% 20%,rgba(37,99,235,0.12),transparent 50%),linear-gradient(180deg,#0b1220 0%,#0f172a 100%)',
        }}
      >
        {/* Grid */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.06) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.06) 1px,transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />

        {/* Welcome bubble */}
        {welcome && !bubbleDismissed && !isOpen && (
          <div
            className="absolute"
            style={{
              bottom: '52px',
              right: '108px',
              maxWidth: 240,
              filter: 'drop-shadow(0 10px 24px rgba(0,0,0,0.35))',
              animation: 'cw-bubble-in 0.5s cubic-bezier(0.34,1.56,0.64,1) both',
            }}
          >
            <div
              className="relative rounded-[18px] bg-white px-4 py-3 pr-9 text-[13.5px] leading-snug font-medium text-slate-900"
              style={{ borderBottomRightRadius: 4 }}
            >
              {welcome}
              <span
                className="absolute block bg-white"
                style={{
                  bottom: 4,
                  right: -6,
                  width: 14,
                  height: 14,
                  transform: 'rotate(45deg)',
                  borderBottomRightRadius: 3,
                }}
              />
              <button
                type="button"
                onClick={() => setBubbleDismissed(true)}
                className="absolute top-1.5 right-1.5 flex h-[22px] w-[22px] items-center justify-center rounded-full bg-black/5 text-slate-600 hover:bg-black/10"
                aria-label="Dismiss"
              >
                <X size={12} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        )}

        {/* FAB */}
        {!isOpen && (
          <button
            type="button"
            onClick={() => {
              setIsOpen(true);
              setBubbleDismissed(true);
            }}
            className="absolute flex items-center justify-center overflow-hidden transition-transform hover:scale-110"
            style={{
              bottom: 28,
              right: 28,
              width: 64,
              height: 64,
              borderRadius: '50%',
              border: 'none',
              cursor: 'pointer',
              background: hasLogo ? '#fff' : `linear-gradient(135deg,${pc},${pc}dd)`,
              boxShadow: `0 6px 24px ${pc}55,0 2px 8px rgba(0,0,0,0.2)`,
              padding: 0,
            }}
          >
            {hasLogo ? (
              <img
                src={branding.logo_url}
                alt=""
                style={{
                  width: '86%',
                  height: '86%',
                  objectFit: 'contain',
                  display: 'block',
                  ...logoExtra,
                }}
              />
            ) : (
              <svg
                width={28}
                height={28}
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth={2}
              >
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            )}
          </button>
        )}

        {/* Panel */}
        {isOpen && (
          <div
            className="absolute top-0 right-0 flex h-full w-[360px] flex-col"
            style={{
              background: 'linear-gradient(180deg,#0f172a 0%,#1e293b 100%)',
              boxShadow: '-8px 0 40px rgba(0,0,0,0.3)',
              animation: 'cw-slide-in 0.35s cubic-bezier(0.16,1,0.3,1)',
            }}
          >
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <div className="flex items-center gap-3">
                {hasLogo && (
                  <div
                    className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-[10px]"
                    style={{ border: '2px solid rgba(255,255,255,0.1)', background: '#fff' }}
                  >
                    <img
                      src={branding.logo_url}
                      alt=""
                      style={{
                        width: '88%',
                        height: '88%',
                        objectFit: 'contain',
                        ...logoExtra,
                      }}
                    />
                  </div>
                )}
                <div>
                  <div className="text-[15px] font-bold tracking-tight text-white">{title}</div>
                  <div className="mt-[2px] text-[10px] font-medium tracking-[0.05em] text-white/50 uppercase">
                    {branding.subtitle || 'Voice Assistant'}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-[10px] border border-white/10 bg-white/5 text-white/60 hover:text-white"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex items-center justify-center gap-2 px-6 py-3 text-xs text-white/50">
              <span
                className="inline-block h-[6px] w-[6px] rounded-full"
                style={{ background: '#6b7280' }}
              />
              Ready to call
            </div>

            <button
              type="button"
              className="mx-6 mb-3 flex items-center justify-center gap-2 rounded-[14px] px-8 py-3 text-[15px] font-semibold text-white"
              style={{
                background: `linear-gradient(135deg,${pc},${pc}cc)`,
                boxShadow: `0 4px 16px ${pc}44`,
                border: 'none',
              }}
            >
              <svg
                width={18}
                height={18}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              Start AI Call
            </button>

            <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-5">
              <div
                className="self-start rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-[13px] leading-relaxed text-white/80"
                style={{ borderBottomLeftRadius: 4, maxWidth: '82%' }}
              >
                Hi! How can I help you today?
              </div>
              <div
                className="self-end rounded-2xl px-4 py-3 text-[13px] leading-relaxed text-white"
                style={{
                  background: pc,
                  borderBottomRightRadius: 4,
                  maxWidth: '82%',
                }}
              >
                Looking for a reliable SUV under £15k.
              </div>
            </div>

            <a
              href="https://carpass.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 border-t border-white/[0.04] px-3 py-1.5 text-[10px] leading-none font-medium tracking-[0.08em] text-white/30 uppercase transition-colors hover:text-white/55"
            >
              <span>Powered by</span>
              <span
                className="inline-flex items-center gap-1 text-[10.5px] leading-none font-bold tracking-[0.02em] normal-case"
                style={{ color: pc }}
              >
                <span
                  className="inline-block h-[4px] w-[4px] rounded-full"
                  style={{ background: pc, boxShadow: `0 0 6px ${pc}99` }}
                />
                Carpass.ai
              </span>
            </a>
          </div>
        )}
      </div>

      <style>{`
        @keyframes cw-bubble-in { 0% { opacity:0; transform:translateY(14px) scale(0.85); } 60% { opacity:1; transform:translateY(-2px) scale(1.02); } 100% { opacity:1; transform:translateY(0) scale(1); } }
        @keyframes cw-slide-in { from { transform:translateX(100%); } to { transform:translateX(0); } }
      `}</style>
    </div>
  );
}
